<template>
  <div class="login-page">
    <div class="login-container">
      <h2 class="login-title">账号登录</h2>
      <form class="login-form">
        <!-- 手机号输入 -->
        <div class="form-group">
          <input
            type="tel"
            v-model="phone"
            placeholder="请输入手机号"
            class="form-control"
            :class="{ 'is-invalid': phoneError }"
          >
          <span class="error-message" v-if="phoneError">{{ phoneError }}</span>
        </div>

        <!-- 验证码输入和发送按钮 -->
        <div class="form-group code-group">
          <input
            type="text"
            v-model="code"
            placeholder="请输入验证码"
            class="form-control code-input"
            :class="{ 'is-invalid': codeError }"
          >
          <button
            type="button"
            class="send-code-btn"
            @click="sendVerificationCode"
            :disabled="isSendingCode || !isPhoneValid"
          >
            {{ codeButtonText }}
          </button>
        </div>

        <span class="error-message" v-if="codeError">{{ codeError }}</span>

        <!-- 登录按钮 -->
        <button
          type="button"
          class="login-btn"
          @click="handleLogin"
          :disabled="loading"
        >
          {{ loading ? '登录中...' : '登录' }}
        </button>

        <!-- 错误提示 -->
        <div class="error-alert" v-if="errorMsg">{{ errorMsg }}</div>
      </form>
    </div>
  </div>
</template>

<script>
import { sendCode, login } from '../api'

export default {
  name: 'LoginPage',
  data() {
    return {
      phone: '',
      code: '',
      loading: false,
      isSendingCode: false,
      countdown: 60,
      codeButtonText: '获取验证码',
      phoneError: '',
      codeError: '',
      errorMsg: ''
    }
  },
  computed: {
    // 检查手机号是否有效
    isPhoneValid() {
      return /^1[3-9]\d{9}$/.test(this.phone)
    }
  },
  methods: {
    // 验证表单
    validateForm() {
      let isValid = true

      // 验证手机号
      if (!this.phone) {
        this.phoneError = '请输入手机号'
        isValid = false
      } else if (!this.isPhoneValid) {
        this.phoneError = '请输入正确的手机号'
        isValid = false
      } else {
        this.phoneError = ''
      }

      // 验证验证码
      if (!this.code) {
        this.codeError = '请输入验证码'
        isValid = false
      } else if (this.code.length !== 6) {
        this.codeError = '验证码长度为6位'
        isValid = false
      } else {
        this.codeError = ''
      }

      return isValid
    },

    // 发送验证码
    async sendVerificationCode() {
      if (!this.isPhoneValid) {
        this.phoneError = '请输入正确的手机号'
        return
      }

      this.isSendingCode = true
      this.codeButtonText = '发送中...'

      try {
        const response = await sendCode(this.phone)
        if (response.code === 200) {
          this.startCountdown()
          this.errorMsg = ''
        } else {
          this.errorMsg = response.message || '发送验证码失败'
        }
      } catch (err) {
        this.errorMsg = err.message || '发送验证码失败，请重试'
        this.isSendingCode = false
        this.codeButtonText = '获取验证码'
      }
    },

    // 验证码倒计时
    startCountdown() {
      this.codeButtonText = `${this.countdown}s后重发`
      const timer = setInterval(() => {
        this.countdown--
        this.codeButtonText = `${this.countdown}s后重发`

        if (this.countdown <= 0) {
          clearInterval(timer)
          this.countdown = 60
          this.codeButtonText = '获取验证码'
          this.isSendingCode = false
        }
      }, 1000)
    },

    // 处理登录
    async handleLogin() {
      if (!this.validateForm()) {
        return
      }

      this.loading = true
      this.errorMsg = ''

      try {
        const response = await login(this.phone, this.code)
        if (response.code === 200 && response.data.token) {
          // 存储token
          localStorage.setItem('token', response.data.token)
          // 跳转到首页
          this.$router.push('/')
        } else {
          throw new Error(response.message || '登录失败')
        }
      } catch (err) {
        this.errorMsg = err.message || '登录失败，请检查验证码'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;
}

.login-container {
  width: 100%;
  max-width: 400px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 30px;
}

.login-title {
  text-align: center;
  margin-bottom: 30px;
  color: #303133;
  font-size: 20px;
  font-weight: 500;
}

.form-group {
  margin-bottom: 20px;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #409eff;
}

.form-control.is-invalid {
  border-color: #f56c6c;
}

.code-group {
  display: flex;
  gap: 10px;
}

.code-input {
  flex: 1;
}

.send-code-btn {
  width: 120px;
  padding: 12px 0;
  background-color: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.send-code-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.login-btn {
  width: 100%;
  padding: 12px 0;
  background-color: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.login-btn:hover:not(:disabled) {
  background-color: #66b1ff;
}

.login-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.error-message {
  display: block;
  margin-top: 5px;
  color: #f56c6c;
  font-size: 12px;
}

.error-alert {
  margin-top: 15px;
  padding: 10px;
  background-color: #fef0f0;
  color: #f56c6c;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
}
</style>
